import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GeneralistCard from "../../components/candidates/generalist_card";
import { useShortlisted } from "../../provider/ShortlistedContext";
import RoleDropBanner from "../../components/role-drop-banner.jsx";
import axios from "axios";
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const CandidatesOfTheWeek = () => {
  const navigate = useNavigate();
  const { shortlistedCount, setShortlistedCount } = useShortlisted();
  
  // State management
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [shortlistedCandidates, setShortlistedCandidates] = useState({});
  const [candidatesStatus, setCandidatesStatus] = useState([]);
  const [rolesNeedUpdate, setRolesNeedUpdate] = useState(false);
  const [newRoleForm, setNewRoleForm] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [navigationIndices, setNavigationIndices] = useState({});
  const [isAnimating, setIsAnimating] = useState({});
  const [showBanner, setShowBanner] = useState(true);
  const [selectedMobileStream, setSelectedMobileStream] = useState(null);

  // Additional state variables needed for shortlist/request functionality
  const [newRoleTitle, setNewRoleTitle] = useState("");
  const [transformedShortlistedCandidates, setTransformedShortlistedCandidates] = useState({});

  // Stream configuration
  const streams = [
    { name: "Generalist", color: "emerald", icon: "🎯" },
    { name: "Sales", color: "blue", icon: "💼" },
    { name: "Software Engineering", color: "orange", icon: "💻" },
    { name: "Marketing", color: "purple", icon: "📈" }
  ];

  // Transform candidate data helper functions
  const transformRoles = (roles) => {
    return roles.map(role => {
      if (role === "Product Manager") return "Product Management";
      if (role === "General Manager") return "General Management"; 
      if (role === "Business Development Manager") return "Business Development";
      if (role === "Operations Manager") return "Operations Management";
      if (role === "Customer Success Manager") return "Customer Success";
      if (role === "Project Manager") return "Project Management";
      if (role === "Marketing Manager") return "Marketing Management";
      if (role === "Account Manager") return "Account Management";
      return role;
    });
  };

  const parseSalaryRange = (salary) => {
    if (!salary) return { minSalary: null, maxSalary: null };
    
    // Handle ranges like "£25k - £35k" 
    const rangeMatch = salary.match(/£?(\d+)k?\s*-\s*£?(\d+)k?/i);
    if (rangeMatch) {
      return {
        minSalary: parseInt(rangeMatch[1]) * 1000,
        maxSalary: parseInt(rangeMatch[2]) * 1000
      };
    }
    
    // Handle single values like "£30k"
    const singleMatch = salary.match(/£?(\d+)k?/i);
    if (singleMatch) {
      const value = parseInt(singleMatch[1]) * 1000;
      return { minSalary: value, maxSalary: value };
    }
    
    return { minSalary: null, maxSalary: null };
  };

  const formatSalaryRange = (minimum, ideal) => {
    if (!minimum && !ideal) return null;
    
    const cleanSalary = (val) => {
      if (!val) return null;
      const num = val.toString().replace(/[^0-9.]/g, '');
      return num ? parseInt(num, 10) : null;
    };
    
    const min = cleanSalary(minimum);
    const max = cleanSalary(ideal);
    
    if (min && max) {
      if (min === max) {
        return `£${min}k`;
      } else {
        return `£${min}k - £${max}k`;
      }
    } else if (min) {
      return `£${min}k+`;
    } else if (max) {
      return `Up to £${max}k`;
    }
    
    return null;
  };

  const transformCandidateFull = (candidate) => {
    if (!candidate || !candidate.fields) {
      return {
        id: candidate?.id || "unknown-id",
        name: candidate?.name || "No data",
        stream: candidate?.stream || "",
      };
    }

    const fields = candidate.fields || {};

    // Safely handle SWE roles
    let sweRoles = [];
    try {
      const rawSweRoles = fields["SWE relevant roles"] || "";
      if (rawSweRoles) {
        sweRoles = Array.isArray(rawSweRoles)
          ? rawSweRoles[0].split(",").map(role => role.trim())
          : typeof rawSweRoles === 'string'
            ? rawSweRoles.split(",").map(role => role.trim())
            : [];
        sweRoles = transformRoles(sweRoles);
      }
    } catch (error) {
      console.error("Error processing SWE roles:", error);
    }

    // Safely extract stream
    let stream = "";
    try {
      if (fields["Stream (from CRM)"]) {
        stream = Array.isArray(fields["Stream (from CRM)"])
          ? fields["Stream (from CRM)"][0]
          : fields["Stream (from CRM)"];
      }
    } catch (error) {
      console.error("Error processing stream:", error);
    }

    // Get image URL safely
    const imageUrl = fields.Photo && fields.Photo[0] ? fields.Photo[0].url : null;

    return {
      id: candidate.id || "",
      url_id: fields["ID"] || "",
      minimum_salary: fields["Minimum salary"] || "",
      ideal_salary: fields["Ideal salary"] || "",
      salary_minimum: fields["Minimum salary"] || "",
      salary_ideal: fields["Ideal salary"] || "",
      expectedSalaryRange: formatSalaryRange(fields["Minimum salary"], fields["Ideal salary"]),

      createdTime: candidate.createdTime || "",
      years_of_exp: fields["Years of exp (or commercial exp)"] || 0,
      cv: fields["CV RAW"] || "",
      video: fields["Video intro (new)"] || "",
      linkedin: fields["LinkedIn RAW"] || "",
      github: fields["Github RAW"] || "",
      
      name: fields["Name"] || "",
      lastName: fields["Surname"] || "",
      email: fields["Email"] || "",
      
      undergrad_degree_type: fields["Undergrad degree type"] || "",
      undergrad_degree_subject: fields["Undergrad subject"] || "",
      undergrad_university: fields["Undergrad university"] || "",
      undergrad_graduation_year: fields["Undergrad graduation year"] || "",
      undergrad_result: fields["Undergrad result"] || "",
      postgrad_degree_type: fields["Postgrad degree type"] || "",
      postgrad_degree_subject: fields["Postgrad subject"] || "",
      postgrad_university: fields["Postgrad university"] || "",
      postgrad_graduation_year: fields["Postgrad graduation year"] || "",
      postgrad_result: fields["Postgrad result"] || "",
      
      roles: fields["Generalist roles suitable for copy"] || "",
      industry_exp: fields["Industry experience new"] || [],
      stream: stream,
      search_fields: fields["Search Aggregate Fields"] || "",

      // Additional fields used by the card
      description: fields["Description"] || fields["Top achievement"] || "",
      most_impressive_swe: fields["Most impressive swe"] || "",
      right_to_work: fields["RTW doc"] || "",
      recentTarget: fields["Recent target achieved"] || "",
      mostRecentCohort: fields["Most recent cohort"] || "",
      
      working_style: fields["Preferred working style copy"] || "",
      languages: fields["Languages copy"] || "",
      time_coding: fields["Time spent coding"] || "",
      coding_languages: fields["Coding languages new"] || "",
      
      // Add Jumpstart view (Sales) field
      "Jumpstart view (Sales)": fields["Jumpstart view (Sales)"] || "",
      
      // Add MRs in last 7 days field for sorting
      mrs_last_7_days: fields["# MRs in last 7 days"] || 0,
      
      work_experience_1_role: fields["(1) Work Experience Role"] || "",
      work_experience_1_company: fields["(1) Work Experience Company"] || "",
      work_experience_1_description: fields["(1) Work Experience Description"] || "",
      work_experience_1_duration: {
        years: fields["(1) Time spent in work experience YRS"] || 0,
        months: fields["(1) Time spent in work experience MOS"] || 0,
      },
      work_experience_2_role: fields["(2) Work Experience Role"] || "",
      work_experience_2_company: fields["(2) Work Experience Company"] || "",
      work_experience_2_description: fields["(2) Work Experience Description"] || "",
      work_experience_2_duration: {
        years: fields["(2) Time spent in work experience YRS"] || 0,
        months: fields["(2) Time spent in work experience MOS"] || 0,
      },

      swe_roles: sweRoles,
      sales: fields["Proven Sales Experience / High Potential for Sales"] || "",
      exp_swe: fields["Level of exp SWE"] || "",
      
      portfolio_swe: fields["SWE Portfolio"] || "",
      marketing_portfolio: fields["Marketing portfolio RAW"] || "",
      
      image: imageUrl,
    };
  };

  // Add toast notification
  const addToast = (message, type = "shortlist") => {
    // Original addToast functionality - now with proper handling for different types
    const newToast = {
      id: Date.now(),
      message: typeof message === 'string' ? message : message.name || 'Candidate',
      type,
    };
    setToasts((prevToasts) => [...prevToasts, newToast]);
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== newToast.id));
    }, 4000);
  };

  // Handle new role creation and submission
  const handleNewRoleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newRoleTitle.trim() || !selectedCandidate) {
      console.error("Missing role title or selected candidate");
      addToast("Error: Role title or candidate missing.", "error");
      return;
    }

    const data = {
      fields: {
        "Role Title": newRoleTitle,
      },
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/roles/createRole", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Add candidate to the new role
      await handleRoleSelect(response.data.newRoleData, selectedCandidate.id);
      
      // Reset states and clear selected candidate
      setRolesNeedUpdate(true);
      setNewRoleForm(false);
      setNewRoleTitle("");
      setSelectedCandidate(null);
      
      // Show success message
      addToast(`Created new role "${newRoleTitle}" and added ${selectedCandidate.name}`, "success");
    } catch (err) {
      console.error(err);
      addToast("Failed to create role. Please try again.", "error");
    }
  };

  // Handle adding candidate to existing role
  const handleRoleSelect = async (role, candidateId) => {
    const updatedData = {
      fields: {
        "Shortlisted Candidates": [candidateId],
      },
      roleId: role["id"],
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        "/api/roles/updateRoleData",
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const updatedRoleData = response.data.updatedRoleData.fields;

      setRoles((prevRoles) => {
        const updatedRoles = prevRoles.map((r) =>
          r.id === updatedRoleData.id ? updatedRoleData : r
        );
        return updatedRoles;
      });

      setRolesNeedUpdate(true);
      
      // Show success toast
      const candidateName = selectedCandidate?.name || "Candidate";
      addToast(`Added ${candidateName} to role: ${role["Role Title"]}`, "success");
    } catch (err) {
      console.error(err);
      addToast("Failed to update role", "error");
    }
  };

  // Handle adding candidate to new role
  const handleAddToNewRole = (candidate) => {
    setSelectedCandidate(candidate);
    setNewRoleForm(true);
  };

  // Handle candidate selection
  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
  };

  // Remove toast
  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch candidates data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch starred and featured candidates
        const response = await axios.get('/api/candidates/fetchCandidates', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { starred: "true" }
        });

        console.log('API Response:', response.data);
        
        if (response.data && response.data.candidates) {
          const transformedCandidates = response.data.candidates.map(candidate => {
            try {
              return transformCandidateFull(candidate);
            } catch (error) {
              console.error('Error transforming candidate:', error, candidate);
              return null;
            }
          }).filter(Boolean);
          
          console.log('Transformed candidates:', transformedCandidates);
          setCandidates(transformedCandidates);
        } else {
          console.log('No candidates data in response');
          setCandidates([]);
        }
      } catch (error) {
        console.error('Error fetching featured candidates:', error);
        setError('Failed to load featured candidates');
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchInterviews = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/interviews/fetchInterviews", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const interviews = response.data.interviews;
        const candidateStatusTuples = interviews.map((interview) => {
          // Handle both flattened and nested field structures
          const candidateId = interview["Candidate"]?.[0] || interview.fields?.["Candidate"]?.[0];
          const stage = interview["Stage"] || interview.fields?.["Stage"];
          const roleTitle = interview["Role Title (from Role)"] || interview.fields?.["Role Title (from Role)"];
          const interviewId = interview["id"] || interview.fields?.["id"] || interview.id;
          
          return {
            id: candidateId,
            status: stage,
            role: roleTitle,
            interviewId: interviewId,
          };
        });

        setCandidatesStatus(candidateStatusTuples);
      } catch (err) {
        console.error('Error fetching interviews:', err);
        // Don't set error state for interviews as it's not critical
      }
    };

    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const rolesResponse = await axios.get("/api/roles/fetchRoles", {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Roles API Response:', rolesResponse.data);
        
        // Check both possible response structures
        if (rolesResponse.data && (rolesResponse.data.roles || rolesResponse.data.success)) {
          const rolesData = rolesResponse.data.roles || [];
          console.log('Setting roles data:', rolesData);
          // Ensure rolesData is always an array
          setRoles(Array.isArray(rolesData) ? rolesData : []);
          
          // Transform shortlisted candidates data to match browse-test.js format
          const transformedShortlisted = {};
          rolesData.forEach(role => {
            if (role["Shortlisted Candidates"]) {
              transformedShortlisted[role["Role Title"] || role.id] = {
                candidates: role["Shortlisted Candidates"],
                roleId: role.id,
                title: role["Role Title"]
              };
            }
          });
          setTransformedShortlistedCandidates(transformedShortlisted);
          setShortlistedCandidates(transformedShortlisted);
        } else {
          console.warn('Unexpected roles API response structure:', rolesResponse.data);
          setRoles([]);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        setRoles([]); // Ensure roles is an empty array rather than undefined
      }
    };

    fetchData();
    fetchInterviews();
    fetchRoles();
  }, []);

  // Refetch roles when rolesNeedUpdate changes
  useEffect(() => {
    if (rolesNeedUpdate) {
      const fetchRolesUpdate = async () => {
        try {
          const token = localStorage.getItem('token');
          const rolesResponse = await axios.get("/api/roles/fetchRoles", {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (rolesResponse.data && (rolesResponse.data.roles || rolesResponse.data.success)) {
            const rolesData = rolesResponse.data.roles || [];
            // Ensure rolesData is always an array
            setRoles(Array.isArray(rolesData) ? rolesData : []);
            setRolesNeedUpdate(false);
            
            // Transform shortlisted candidates data to match browse-test.js format
            const transformedShortlisted = {};
            rolesData.forEach(role => {
              if (role["Shortlisted Candidates"]) {
                transformedShortlisted[role["Role Title"] || role.id] = {
                  candidates: role["Shortlisted Candidates"],
                  roleId: role.id,
                  title: role["Role Title"]
                };
              }
            });
            setTransformedShortlistedCandidates(transformedShortlisted);
            setShortlistedCandidates(transformedShortlisted);
          }
        } catch (error) {
          console.error('Error refetching roles:', error);
          setRolesNeedUpdate(false);
        }
      };
      
      fetchRolesUpdate();
    }
  }, [rolesNeedUpdate]);

  // Group candidates by stream and sort by lowest MRs in last 7 days first
  const candidatesByStream = candidates.reduce((acc, candidate) => {
    const stream = candidate.stream || "Other";
    if (!acc[stream]) {
      acc[stream] = [];
    }
    acc[stream].push(candidate);
    return acc;
  }, {});

  // Sort candidates within each stream by lowest MRs in last 7 days first
  Object.keys(candidatesByStream).forEach(stream => {
    candidatesByStream[stream].sort((a, b) => {
      const aMRs = parseInt(a.mrs_last_7_days) || 0;
      const bMRs = parseInt(b.mrs_last_7_days) || 0;
      return aMRs - bMRs; // Ascending order (lowest first)
    });
  });

  // Calculate available streams and related variables after candidatesByStream is defined
  const availableStreams = streams.filter(s => (candidatesByStream[s.name] || []).length > 0);
  const streamNames = availableStreams.map(s => s.name);
  const formattedStreamNames = streamNames.length > 1
    ? `${streamNames.slice(0, -1).join(', ')}, and ${streamNames.slice(-1)}`
    : streamNames[0] || '';

  // Initialize selectedMobileStream with the first available stream
  React.useEffect(() => {
    if (availableStreams.length > 0 && !selectedMobileStream) {
      setSelectedMobileStream(availableStreams[0].name);
    }
  }, [availableStreams, selectedMobileStream]);

  // Debug logging to help identify issues
  React.useEffect(() => {
    console.log('CandidatesOfTheWeek - Debug Info:', {
      candidatesCount: candidates.length,
      rolesCount: Array.isArray(roles) ? roles.length : 0,
      roles: Array.isArray(roles) ? roles.slice(0, 3) : [], // Log first 3 roles for inspection
      rolesIsArray: Array.isArray(roles),
      shortlistedCandidatesCount: Object.keys(shortlistedCandidates).length,
      rolesNeedUpdate: rolesNeedUpdate,
    });
  }, [candidates, roles, shortlistedCandidates, rolesNeedUpdate]);

  // Get stream color classes
  const getStreamColorClasses = (streamName) => {
    switch (streamName) {
      case "Generalist":
        return {
          bg: "bg-emerald-50",
          border: "border-emerald-200",
          text: "text-emerald-800",
          accent: "bg-emerald-500",
          hover: "hover:bg-emerald-100"
        };
      case "Sales":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          text: "text-blue-800",
          accent: "bg-blue-500",
          hover: "hover:bg-blue-100"
        };
      case "Software Engineering":
        return {
          bg: "bg-orange-50",
          border: "border-orange-200",
          text: "text-orange-800",
          accent: "bg-orange-500",
          hover: "hover:bg-orange-100"
        };
      case "Marketing":
        return {
          bg: "bg-purple-50",
          border: "border-purple-200",
          text: "text-purple-800",
          accent: "bg-purple-500",
          hover: "hover:bg-purple-100"
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          text: "text-gray-800",
          accent: "bg-gray-500",
          hover: "hover:bg-gray-100"
        };
    }
  };

  if (loading) {
    return (
      <div className="flex-grow bg-white min-h-screen">
        <main className="py-4 md:py-10 lg:pl-56">
          <div className="flex justify-center items-center h-[70vh] w-full">
            <img 
              src="/images/coding_logo/Untitled design (3).gif" 
              alt="Loading..." 
              className="w-64 h-64 object-contain"
            />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading featured candidates: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <RoleDropBanner className="hidden md:block" />
      {/* Mobile scrollbar hide styles and animations */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .stream-indicator {
          animation: streamPulse 2s ease-in-out infinite;
          box-shadow: 0 0 0 0 rgba(var(--indicator-color), 0.3);
        }
        @keyframes streamPulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(var(--indicator-color), 0.3);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 0 0 8px rgba(var(--indicator-color), 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(var(--indicator-color), 0);
          }
        }
      `}</style>
      
      <div className="flex-grow bg-white min-h-screen">
      <main className="py-4 md:py-10 lg:pl-56 md:mt-6">
        <div className="max-w-7xl mx-auto px-4 md:px-2 sm:px-4 lg:px-6">
          
          {/* Page header */}
          <div className="pb-4 md:pb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl">
              🌟 Standout Candidates 
              </h2>
                              <p className="mt-1 md:mt-2 text-sm md:text-base">
                <b>New week, new standouts.</b> These candidates impressed us — and likely won't be around for long. Add to your shortlist or request directly.
                </p>
            </div>
            
            {/* Header buttons - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-2 md:gap-3">
              <button
                onClick={() => navigate('/browse-candidates')}
                className="px-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium whitespace-nowrap"
              >
                View All Candidates
              </button>
              <button
                onClick={() => navigate('/shortlists')}
                className="px-4 py-2.5 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
                </svg>
                My Shortlist
              </button>
            </div>
          </div>

          {/* Desktop header divider line */}
          <div className="hidden md:block border-b border-gray-200 mb-6 -mx-4 md:-mx-2 sm:-mx-4 lg:-mx-6"></div>

          {/* Toast notifications */}
          {toasts.length > 0 && (
            <>
              <div className="fixed inset-0 z-40 bg-black opacity-10 pointer-events-none"></div>
              <div className="relative z-50">
                <div className="fixed top-0 right-0 flex flex-col items-end p-6 space-y-3">
                  {toasts.map((toast, index) => {
                    let iconContent = null;
                    let messageContent = null;
                    let actionButton = null;
                    
                    if (toast.type === "success") {
                      iconContent = (
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      );
                      messageContent = <span className="font-normal">{toast.message}</span>;
                      actionButton = null;
                    } else if (toast.type === "error") {
                      iconContent = (
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                        </svg>
                      );
                      messageContent = <span className="font-normal">{toast.message}</span>;
                      actionButton = null;
                    } else if (toast.type === "shortlist") {
                      iconContent = (
                        <svg
                          className="text-green-500 mr-1 h-5 w-5"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                        </svg>
                      );
                      messageContent = (
                        <>
                          <span className="font-bold">{toast.message}</span>&nbsp;successfully added to shortlist.
                        </>
                      );
                      actionButton = (
                        <button
                          onClick={() => navigate("/shortlists")}
                          className="text-sm flex font-medium text-emerald-500 hover:bg-gray-100 rounded-lg p-1.5"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-5 mr-1"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                            />
                          </svg>
                          View shortlists
                        </button>
                      );
                    }
                    
                    return (
                      <div
                        key={index}
                        className="fixed divide-x flex right-10 border z-50 justify-between items-center w-full max-w-[500px] p-4 bg-white rounded-lg shadow-lg mb-4"
                        role="alert"
                        style={{ top: `${50 + index * 70}px` }}
                      >
                        <div className="text-sm font-normal text-gray-500 flex">
                          {iconContent}
                          {messageContent}
                        </div>
                        {actionButton && (
                          <div className="flex items-center space-x-2 pl-2">
                            {actionButton}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Mobile Stream Tabs - Only visible on mobile */}
          <div className="md:hidden mb-4">
            <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-200">
              {availableStreams.map(stream => {
                const isActive = selectedMobileStream === stream.name;
                const colors = getStreamColorClasses(stream.name);
                const streamCandidateCount = candidatesByStream[stream.name]?.length || 0;
                return (
                  <button
                    key={stream.name}
                    onClick={() => setSelectedMobileStream(stream.name)}
                    className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 ease-out relative ${
                      isActive 
                        ? `bg-white ${colors.text} shadow-lg scale-[1.02] border-2 ${colors.border}` 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                    }`}
                    style={isActive ? {
                      boxShadow: `0 8px 25px -8px ${
                        stream.name === 'Generalist' ? 'rgba(16, 185, 129, 0.35)' :
                        stream.name === 'Sales' ? 'rgba(59, 130, 246, 0.35)' :
                        stream.name === 'Software Engineering' ? 'rgba(249, 115, 22, 0.35)' :
                        stream.name === 'Marketing' ? 'rgba(147, 51, 234, 0.35)' : 'rgba(107, 114, 128, 0.35)'
                      }`
                    } : {}}
                  >
                    <span className="text-xs leading-tight">{stream.name}</span>
                    <span className={`absolute top-1 right-1 text-xs px-1.5 py-0.5 rounded-full font-medium min-w-[1.25rem] h-5 flex items-center justify-center ${
                      isActive 
                        ? `bg-gray-100 ${colors.text}` 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {streamCandidateCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mobile Single Card View with Horizontal Scroll - Only visible on mobile */}
          <div className="md:hidden">
            {availableStreams.map(stream => {
              const streamCandidates = candidatesByStream[stream.name] || [];
              
              if (streamCandidates.length === 0 || selectedMobileStream !== stream.name) return null;

              return (
                <div key={stream.name} className="relative">
                  <div className="overflow-x-auto overflow-y-hidden scrollbar-hide pb-4" style={{ 
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    <div className="flex gap-4 pl-4 pr-16">
                      {streamCandidates.map((candidate, index) => (
                        <div 
                          key={candidate.id} 
                          className="flex-shrink-0"
                          style={{ 
                            width: 'calc(100vw - 5rem)',
                            scrollSnapAlign: 'start'
                          }}
                        >
                          <GeneralistCard
                            candidate={candidate}
                            roles={roles}
                            shortlistedCandidates={shortlistedCandidates}
                            setRoles={setRoles}
                            setShortlistedCandidates={setShortlistedCandidates}
                            setRolesNeedUpdate={setRolesNeedUpdate}
                            setNewRoleForm={setNewRoleForm}
                            handleSelectCandidate={handleSelectCandidate}
                            candidatesStatus={candidatesStatus}
                            addToast={addToast}
                            handleAddToNewRole={handleAddToNewRole}
                            displayShortlistButton={true}
                            showRecommendationInfo={false}
                            useColoredShadow={true}
                            showFeaturedTag={false}
                            hideStreamTag={true}
                            hideRecentlyAddedTag={true}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Streams sections - Only visible on desktop */}
          <div className="hidden md:block space-y-16">
            {streams.map(stream => {
              const streamCandidates = candidatesByStream[stream.name] || [];
              
              if (streamCandidates.length === 0) return null;

              const colors = getStreamColorClasses(stream.name);
              const currentIndex = navigationIndices[stream.name] || 0;
              const cardsPerPage = 2;
              const totalPages = Math.ceil(streamCandidates.length / cardsPerPage);
              const streamIsAnimating = isAnimating[stream.name] || false;

              const changePage = (direction = 'next') => {
                setIsAnimating(prev => ({ ...prev, [stream.name]: true })); // Start fade out

                setTimeout(() => {
                  let newIndex;
                  if (direction === 'next') {
                    newIndex = currentIndex + cardsPerPage >= streamCandidates.length ? 0 : currentIndex + cardsPerPage;
                  } else {
                    newIndex = currentIndex - cardsPerPage < 0 ? Math.max(0, streamCandidates.length - cardsPerPage) : currentIndex - cardsPerPage;
                  }
                  
                  setNavigationIndices(prev => ({
                    ...prev,
                    [stream.name]: newIndex
                  }));

                  // Short delay to allow React to re-render with new items before fading in
                  setTimeout(() => {
                    setIsAnimating(prev => ({ ...prev, [stream.name]: false }));
                  }, 30);

                }, 150);
              };

              const visibleCandidates = streamCandidates.slice(currentIndex, currentIndex + cardsPerPage);

              return (
                <div key={stream.name} className="relative">
                  {/* Stream header */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className={`w-4 h-4 rounded-full ${colors.accent} stream-indicator`}
                          style={{
                            '--indicator-color': stream.name === 'Generalist' ? '16, 185, 129' :
                                                stream.name === 'Sales' ? '59, 130, 246' :
                                                stream.name === 'Software Engineering' ? '249, 115, 22' :
                                                stream.name === 'Marketing' ? '147, 51, 234' : '107, 114, 128'
                          }}
                        ></div>
                        <h2 className="text-2xl font-medium text-gray-900">
                          Top {stream.name} Candidates
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          {streamCandidates.length} Profile{streamCandidates.length !== 1 ? 's' : ''} 
                        </p>
                      </div>
                      
                      {/* Navigation arrows - positioned on the right */}
                      {streamCandidates.length > cardsPerPage && (
                        <div className="flex items-center gap-2 mr-8 mt-2">
                          <button
                            onClick={() => changePage('back')}
                            className="group p-3 rounded-xl bg-white shadow-lg shadow-gray-200/50 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-200/30 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 ease-out disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:scale-100 disabled:hover:shadow-lg disabled:hover:border-gray-200"
                            disabled={streamIsAnimating || currentIndex === 0}
                            aria-label="Previous candidates"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5 text-gray-700 group-hover:text-gray-900 transition-colors duration-300">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                            </svg>
                          </button>
                          
                          <span className="text-sm text-gray-500 px-2">
                            {Math.floor(currentIndex / cardsPerPage) + 1} / {totalPages}
                          </span>
                          
                          <button
                            onClick={() => changePage('next')}
                            className="group p-3 rounded-xl bg-white shadow-lg shadow-gray-200/50 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-200/30 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 ease-out disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:scale-100 disabled:hover:shadow-lg disabled:hover:border-gray-200"
                            disabled={streamIsAnimating || currentIndex + cardsPerPage >= streamCandidates.length}
                            aria-label="Next candidates"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5 text-gray-700 group-hover:text-gray-900 transition-colors duration-300">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Centered Candidates grid */}
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 justify-center transition-all duration-150 ease-out ${streamIsAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                    {visibleCandidates.map((candidate) => (
                      <div key={candidate.id}>
                        <GeneralistCard
                          candidate={candidate}
                          roles={roles}
                          shortlistedCandidates={shortlistedCandidates}
                          setRoles={setRoles}
                          setShortlistedCandidates={setShortlistedCandidates}
                          setRolesNeedUpdate={setRolesNeedUpdate}
                          setNewRoleForm={setNewRoleForm}
                          handleSelectCandidate={handleSelectCandidate}
                          candidatesStatus={candidatesStatus}
                          addToast={addToast}
                          handleAddToNewRole={handleAddToNewRole}
                          displayShortlistButton={true}
                          showRecommendationInfo={false}
                          useColoredShadow={true}
                          showFeaturedTag={false}
                          hideStreamTag={true}
                          hideRecentlyAddedTag={true}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* No candidates message */}
          {Object.keys(candidatesByStream).length === 0 && (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                <StarIcon className="text-gray-400" fontSize="large" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No featured candidates available
              </h3>
              <p className="text-gray-600">
                No candidates are currently both starred and featured. Check back later for new featured candidates.
              </p>
            </div>
          )}

          {/* Call to action */}

        </div>
      </main>

      {/* New Role Form Modal */}
      {newRoleForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 9999 }}>
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h2 className="text-lg font-semibold mb-4">Add New Role</h2>
            <form onSubmit={handleNewRoleSubmit}>
              <label
                htmlFor="title"
                className="block text-md font-normal leading-6 text-gray-900"
              >
                Role Title
              </label>
              <input
                type="text"
                value={newRoleTitle}
                onChange={(e) => setNewRoleTitle(e.target.value)}
                placeholder="Sales"
                className="w-full p-2 mb-2 border border-gray-300 rounded"
                required
              />
              <button
                type="submit"
                className="block w-full px-4 py-2 bg-emerald-500 text-sm text-white rounded hover:bg-emerald-600"
              >
                Submit
              </button>
              <button
                type="button"
                onClick={() => setNewRoleForm(false)}
                className="block w-full px-4 py-2 mt-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CandidatesOfTheWeek;